// 一致情報の行をクリックしたとき、詳細ペインを該当箇所まで移動して一時的に強調する
import { nextTick } from 'vue';

// hit.source → 行き先のタブと、data-detail-anchor の値
// body は「本文」タブを開いて該当ページの行までスクロールする (jumpToHit 内で個別処理)
// title / id は対象外 (クリックできる要素にもしない)
const JUMP_TARGETS = {
  chapter: { tab: 'chapters' },
  transcript: { tab: 'transcript' },
  ai_description: { tab: 'overview', anchor: 'ai_description' },
  ai_genre: { tab: 'overview', anchor: 'ai_genre' },
  ai_attribute: { tab: 'tags', anchor: 'ai_attribute' },
  description: { tab: 'info', anchor: 'description' },
  tag: { tab: 'info', anchor: 'tag' },
  uploader: { tab: 'info', anchor: 'uploader' },
  cloud_path: { tab: 'info', anchor: 'cloud_path' },
  body: { tab: 'body' },
  // cad_view は図面情報タブを開いてビューの見出しへ、cad_tag は図面タグタブを開いてタグの節へスクロールする
  cad_view: { tab: 'cad_info' },
  cad_tag: { tab: 'cad_tags' },
};

// AI 解析データが揃っていないと中身が描画されないタブ
const AI_TABS = ['overview', 'chapters', 'transcript', 'tags'];
const HIGHLIGHT_CLASS = 'detail-jump-highlight';

export const useDetailPaneJump = ({
  selectedItem,
  detailSubTab,
  aiAnalysisData,
  fetchAiAnalysis,
  // useBodyText の ensureBodyPageLoaded。3 つの composable の依存が循環しているため
  // 生成後に app.js から差し込まれる ref (生成時点では null)
  ensureBodyPageRef,
  // useCadInfo の ensureCadInfoLoaded。ensureBodyPageRef と同じく生成後に差し込まれる ref
  ensureCadInfoRef,
}) => {
  // 連打されたとき、後からクリックした方だけを有効にするための通し番号
  let jumpSeq = 0;

  const isJumpableHit = (hit) =>
    !!hit && Object.prototype.hasOwnProperty.call(JUMP_TARGETS, hit.source);

  const flash = (element) => {
    if (!element) {
      return;
    }
    // 直前の強調が残っているとアニメーションが再生されないため、一度外してリフローを挟む
    element.classList.remove(HIGHLIGHT_CLASS);
    void element.offsetWidth;
    element.classList.add(HIGHLIGHT_CLASS);
    element.addEventListener(
      'animationend',
      () => {
        element.classList.remove(HIGHLIGHT_CLASS);
      },
      { once: true }
    );
  };

  const scrollToElement = (element, block = 'center') => {
    if (!element || typeof element.scrollIntoView !== 'function') {
      return;
    }
    element.scrollIntoView({ behavior: 'smooth', block, inline: 'nearest' });
    flash(element);
  };

  const findAnchor = (name) => document.querySelector(`[data-detail-anchor="${name}"]`);

  // exa_video_text_segments.start_sec は整数で保存されている。
  // 画面側の chapter.start_sec / seg.start は小数のことがあるため切り捨てて比べる。
  // Number(null) と Number('') は 0 になるので、開始秒数を持たない章 (ページ番号だけの
  // チャプター) が 0 秒の章として扱われないよう、先に弾く
  const truncSeconds = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const num = Number(value);
    return Number.isFinite(num) ? Math.trunc(num) : null;
  };

  const findChapterIndex = (hitStartSec) => {
    const chapters = aiAnalysisData.value?.chapters;
    if (!Array.isArray(chapters) || hitStartSec === null) {
      return -1;
    }
    const exact = chapters.findIndex((chapter) => truncSeconds(chapter?.start_sec) === hitStartSec);
    if (exact >= 0) {
      return exact;
    }
    // チャプター内のイベント (time_sec) で当たった場合は、その時刻を含むチャプターに落とす
    let fallback = -1;
    chapters.forEach((chapter, idx) => {
      const start = truncSeconds(chapter?.start_sec);
      if (start === null || start > hitStartSec) {
        return;
      }
      if (fallback < 0 || start >= truncSeconds(chapters[fallback]?.start_sec)) {
        fallback = idx;
      }
    });
    return fallback;
  };

  const findTranscriptIndex = (hitStartSec) => {
    const segments = aiAnalysisData.value?.transcript_segments;
    if (!Array.isArray(segments) || hitStartSec === null) {
      return -1;
    }
    return segments.findIndex((segment) => truncSeconds(segment?.start) === hitStartSec);
  };

  // チャプターは Bootstrap のアコーディオン。show クラスを手で足すと他のパネルが
  // 開いたままになり Bootstrap 側の状態ともずれるため collapse('show') を使う
  const openChapter = (idx) => {
    const head = document.getElementById(`ch-head-${idx}`);
    const body = document.getElementById(`ch-body-${idx}`);
    const jquery = window.jQuery;
    if (!body || !jquery || body.classList.contains('show')) {
      scrollToElement(head);
      return;
    }
    // 開くアニメーションの最中は見出しの位置が動くので、開き終わってからスクロールする
    jquery(body).one('shown.bs.collapse', () => {
      scrollToElement(head);
    });
    jquery(body).collapse('show');
  };

  const jumpToHit = async (hit) => {
    if (!isJumpableHit(hit)) {
      return;
    }
    const item = selectedItem.value;
    if (!item) {
      return;
    }
    const target = JUMP_TARGETS[hit.source];
    const needsAiAnalysis = AI_TABS.includes(target.tab);
    // AI のタブはボタン自体が ai_analysis_status === 'analyzed' のときしか出ない。
    // 解析済みでないファイルではタブを切り替えず、従来の挙動 (シーク) だけを残す
    if (needsAiAnalysis && item.ai_analysis_status !== 'analyzed') {
      return;
    }

    const seq = ++jumpSeq;
    const itemId = String(item.id);

    if (hit.source === 'body') {
      // 本文タブが無いファイルでは従来どおり PDF のページ送りだけを残す
      if (!item.has_body_text) {
        return;
      }
      // detailSubTab への代入と同じ同期ブロックで呼ぶ。間に await を挟むと、
      // useBodyText の watch(detailSubTab) が先に走って無駄な取得と競合する
      detailSubTab.value = target.tab;
      const bodyPagePromise = ensureBodyPageRef.value?.(hit.page_no);
      await bodyPagePromise;
      if (seq !== jumpSeq || String(selectedItem.value?.id) !== itemId) {
        return;
      }
      await nextTick();
      if (seq !== jumpSeq || String(selectedItem.value?.id) !== itemId) {
        return;
      }
      // 1 ページで数十万文字になる書類があり、要素の高さが数万 px になるため
      // 'center' では狙った行が画面外に出る。'start' で行の先頭を画面上端に合わせる
      scrollToElement(document.querySelector(`[data-body-page="${hit.page_no}"]`), 'start');
      return;
    }

    if (hit.source === 'cad_view' || hit.source === 'cad_tag') {
      // 移動先のタブ (cad_view は図面情報、cad_tag は図面タグ) が無いファイルでは何もしない
      if (hit.source === 'cad_view' ? !item.has_cad_info : !item.has_cad_tags) {
        return;
      }
      // body と同じく、detailSubTab への代入と同じ同期ブロックで取得を呼ぶ
      detailSubTab.value = target.tab;
      await ensureCadInfoRef.value?.();
      if (seq !== jumpSeq || String(selectedItem.value?.id) !== itemId) {
        return;
      }
      await nextTick();
      if (seq !== jumpSeq || String(selectedItem.value?.id) !== itemId) {
        return;
      }
      const selector = hit.source === 'cad_view'
        ? `[data-cad-view="${hit.page_no}"]`
        : '[data-cad-tags]';
      scrollToElement(document.querySelector(selector), 'start');
      return;
    }

    detailSubTab.value = target.tab;

    if (needsAiAnalysis) {
      await fetchAiAnalysis(item.id);
      // 待っている間に別の行がクリックされた / 選択ファイルが変わったら何もしない
      if (seq !== jumpSeq || String(selectedItem.value?.id) !== itemId) {
        return;
      }
    }

    await nextTick();
    if (seq !== jumpSeq || String(selectedItem.value?.id) !== itemId) {
      return;
    }

    const hitStartSec = hit.start_sec === null || hit.start_sec === undefined
      ? null
      : truncSeconds(hit.start_sec);

    if (hit.source === 'chapter') {
      const idx = findChapterIndex(hitStartSec);
      if (idx >= 0) {
        openChapter(idx);
      }
      return;
    }

    if (hit.source === 'transcript') {
      const idx = findTranscriptIndex(hitStartSec);
      if (idx >= 0) {
        scrollToElement(document.querySelector(`[data-detail-seg="${idx}"]`));
      }
      return;
    }

    scrollToElement(findAnchor(target.anchor));
  };

  // 図面情報タブのビュー一覧のボタンから呼ばれる。そのビューの見出しへスクロールして強調する
  const scrollToCadView = (viewNo) => {
    scrollToElement(document.querySelector(`[data-cad-view="${viewNo}"]`), 'start');
  };

  return {
    isJumpableHit,
    jumpToHit,
    scrollToCadView,
  };
};
